<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { collection, query, orderBy, limit, getDocs, where } from '@/lib/firestore'
import { db } from '@/config/firebase'
import { getTenantConstraint } from '@/utils/tenantQuery'
import { useHelpCenter } from '@/composables/useHelpCenter'
import { MdPreview } from 'md-editor-v3'
import 'md-editor-v3/lib/preview.css'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent } from '@/components/ui/card'
import HelpCenterIntro from '@/components/HelpCenterIntro.vue'
import {
  X,
  Minus,
  Search,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Home,
  Rocket,
  LayoutDashboard,
  Building2,
  FolderKanban,
  ListTodo,
  ClipboardList,
  Layers,
  MessageSquarePlus,
  Ticket,
  PackageCheck,
  Users,
  Calendar,
  Video,
  FileText,
  Receipt,
  ShoppingCart,
  UserCog,
  Settings,
  Bot,
  Shield,
  History,
  MessageCircle,
  Play,
  ExternalLink,
  Sparkles,
  Star,
  TrendingUp,
  Heart,
  Newspaper,
  Library,
} from 'lucide-vue-next'

import ChangelogSection from '@/views/Help/components/ChangelogSection.vue'
import FeedbackSection from '@/views/Help/components/FeedbackSection.vue'
import NewsSection from '@/views/Help/components/NewsSection.vue'
import HelpModulesDrawer from '@/components/HelpModulesDrawer.vue'

import type { HelpGuide, ActionLink } from '@/types/help'

const router = useRouter()

const {
  state,
  availableModules,
  availableGuides,
  currentModuleGuides,
  currentGuide,
  searchResults,
  close,
  minimize,
  navigateToGuide,
  navigateToModule,
  navigateToChangelog,
  navigateToFeedback,
  navigateToNews,
  setSearchQuery,
  goHome,
  shouldShowIntro,
  markIntroSeen,
} = useHelpCenter()

// Estado del drawer de módulos
const isModulesDrawerOpen = ref(false)

// Control de la intro
const showingIntro = computed(() => state.value.isOpen && shouldShowIntro() && !state.value.isMinimized)

const handleIntroComplete = () => {
  // La intro ya marcó como vista, solo cerramos el estado
}

// Datos para widgets del home
const latestVersion = ref<{ version: string; notes: string; releasedAt: Date } | null>(null)
const feedbackStats = ref<{ total: number; pending: number; resolved: number; avgRating: number; ratingCount: number }>({
  total: 0,
  pending: 0,
  resolved: 0,
  avgRating: 0,
  ratingCount: 0,
})

// Cargar datos al montar
onMounted(async () => {
  // Cargar última versión
  try {
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []
    const appsQuery = query(
      collection(db, 'applications'),
      ...baseConstraints,
      orderBy('updatedAt', 'desc'),
      limit(1)
    )
    const snapshot = await getDocs(appsQuery)
    if (!snapshot.empty) {
      const data = snapshot.docs[0].data()
      const versionHistory = data.versionHistory || []
      if (versionHistory.length > 0) {
        const latest = versionHistory.sort(
          (a: { releasedAt?: { toDate?: () => Date } }, b: { releasedAt?: { toDate?: () => Date } }) => {
            const dateA = a.releasedAt?.toDate?.()?.getTime() || 0
            const dateB = b.releasedAt?.toDate?.()?.getTime() || 0
            return dateB - dateA
          }
        )[0]
        latestVersion.value = {
          version: latest.version,
          notes: latest.notes || '',
          releasedAt: latest.releasedAt?.toDate?.() || new Date(),
        }
      }
    }
  } catch (e) {
    console.error('Error loading version:', e)
  }

  // Cargar stats de feedback
  try {
    const feedbackQuery = query(collection(db, 'userFeedback'))
    const feedbackSnap = await getDocs(feedbackQuery)
    const total = feedbackSnap.size
    let pending = 0
    let resolved = 0
    let ratingSum = 0
    let ratingCount = 0
    feedbackSnap.forEach((doc) => {
      const data = doc.data()
      const status = data.status
      if (status === 'new' || status === 'in_progress') pending++
      if (status === 'resolved') resolved++
      // Calcular promedio de ratings
      if (data.type === 'rating' && data.rating) {
        ratingSum += data.rating
        ratingCount++
      }
    })
    feedbackStats.value = {
      total,
      pending,
      resolved,
      avgRating: ratingCount > 0 ? ratingSum / ratingCount : 0,
      ratingCount,
    }
  } catch (e) {
    console.error('Error loading feedback stats:', e)
  }
})

const iconMap: Record<string, unknown> = {
  Rocket,
  LayoutDashboard,
  Building2,
  FolderKanban,
  ListTodo,
  ClipboardList,
  Layers,
  MessageSquarePlus,
  Ticket,
  PackageCheck,
  Users,
  Calendar,
  Video,
  FileText,
  Receipt,
  ShoppingCart,
  UserCog,
  Settings,
  Bot,
  Shield,
}

const getIcon = (iconName: string) => {
  return iconMap[iconName] || FileText
}

// Guías relacionadas
const relatedGuides = computed<HelpGuide[]>(() => {
  if (!currentGuide.value?.relatedGuides) return []
  return currentGuide.value.relatedGuides
    .map((slug) => availableGuides.value.find((g) => g.slug === slug))
    .filter((g): g is HelpGuide => !!g)
})

// Navegación prev/next dentro del módulo
const currentIndex = computed(() => {
  if (!currentGuide.value) return -1
  return currentModuleGuides.value.findIndex((g) => g.slug === currentGuide.value!.slug)
})

const prevGuide = computed(() => {
  if (currentIndex.value <= 0) return null
  return currentModuleGuides.value[currentIndex.value - 1]
})

const nextGuide = computed(() => {
  if (currentIndex.value < 0 || currentIndex.value >= currentModuleGuides.value.length - 1) return null
  return currentModuleGuides.value[currentIndex.value + 1]
})

const handleSearch = (e: Event) => {
  setSearchQuery((e.target as HTMLInputElement).value)
}

const currentModuleTitle = computed(() => {
  if (!state.value.currentModule) return ''
  return availableModules.value.find((m) => m.id === state.value.currentModule)?.title || ''
})

const roleLabels: Record<string, string> = {
  superadmin: 'Super Admin',
  tenant_admin: 'Tenant Admin',
  admin: 'Admin',
  operator: 'Operador',
  client: 'Cliente',
  member: 'Miembro',
}

// Video helpers
const isYouTube = (url: string): boolean => {
  return url.includes('youtube.com') || url.includes('youtu.be')
}

const getYouTubeEmbed = (url: string): string => {
  let videoId = ''
  if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1].split('?')[0]
  } else if (url.includes('watch?v=')) {
    videoId = url.split('watch?v=')[1].split('&')[0]
  } else if (url.includes('/embed/')) {
    videoId = url.split('/embed/')[1].split('?')[0]
  }
  return `https://www.youtube.com/embed/${videoId}`
}

// Action link handler
const handleActionLink = (link: ActionLink) => {
  minimize()
  router.push(link.route)
}

// Formato de fecha
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

// Check si estamos en una vista que no es home
const isNotHome = computed(() => {
  return state.value.currentModule || state.value.currentGuide ||
         state.value.currentView === 'changelog' || state.value.currentView === 'feedback' ||
         state.value.currentView === 'news'
})
</script>

<template>
  <Teleport to="body">
    <!-- Animación de Intro (primera vez) -->
    <HelpCenterIntro
      v-if="showingIntro"
      @complete="handleIntroComplete"
    />

    <!-- Panel Principal -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-200 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="state.isOpen && !showingIntro" class="fixed inset-0 z-[100] bg-background flex flex-col">
        <!-- Header -->
        <header class="h-14 border-b flex items-center justify-between px-4 shrink-0">
          <div class="flex items-center gap-3">
            <!-- Botón Home mejorado -->
            <Button
              v-if="isNotHome"
              variant="outline"
              size="sm"
              class="gap-2"
              @click="goHome"
            >
              <Home class="h-4 w-4" />
              <span class="hidden sm:inline">Inicio</span>
            </Button>
            <BookOpen class="h-5 w-5 text-primary" />
            <h1 class="font-semibold">Centro de Ayuda</h1>
          </div>
          <div class="flex items-center gap-1">
            <Button variant="ghost" size="icon" @click="minimize" title="Minimizar">
              <Minus class="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" @click="close" title="Cerrar">
              <X class="h-4 w-4" />
            </Button>
          </div>
        </header>

        <!-- Content -->
        <div class="flex-1 flex min-h-0">
          <!-- Sidebar -->
          <aside class="w-64 border-r flex flex-col shrink-0">
            <!-- Search -->
            <div class="p-3 border-b">
              <div class="relative">
                <Search class="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  :model-value="state.searchQuery"
                  placeholder="Buscar..."
                  class="pl-8 h-9"
                  @input="handleSearch"
                />
              </div>
            </div>

            <!-- Search Results -->
            <ScrollArea v-if="state.searchQuery" class="flex-1">
              <div class="p-2">
                <p class="text-xs text-muted-foreground px-2 mb-2">
                  {{ searchResults.length }} resultado{{ searchResults.length !== 1 ? 's' : '' }}
                </p>
                <button
                  v-for="guide in searchResults"
                  :key="guide.slug"
                  class="w-full text-left px-2 py-1.5 rounded hover:bg-muted text-sm"
                  @click="navigateToGuide(guide.slug)"
                >
                  <p class="font-medium truncate">{{ guide.title }}</p>
                  <p class="text-xs text-muted-foreground truncate">{{ guide.category }}</p>
                </button>
                <p
                  v-if="searchResults.length === 0"
                  class="text-sm text-muted-foreground px-2 py-4 text-center"
                >
                  No se encontraron resultados
                </p>
              </div>
            </ScrollArea>

            <!-- Navigation Menu -->
            <ScrollArea v-else class="flex-1">
              <div class="p-2">
                <!-- Navegación Principal -->
                <p class="text-xs font-medium text-muted-foreground px-2 mb-2">NAVEGACIÓN</p>

                <button
                  class="w-full flex items-center gap-2 px-2 py-2 rounded text-sm"
                  :class="state.currentView === 'home' && !state.currentModule ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'"
                  @click="goHome"
                >
                  <Home class="h-4 w-4" />
                  <span>Inicio</span>
                </button>

                <button
                  class="w-full flex items-center gap-2 px-2 py-2 rounded text-sm"
                  :class="state.currentView === 'news' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'"
                  @click="navigateToNews"
                >
                  <Newspaper class="h-4 w-4" />
                  <span>Noticias</span>
                </button>

                <button
                  class="w-full flex items-center gap-2 px-2 py-2 rounded text-sm"
                  :class="state.currentView === 'changelog' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'"
                  @click="navigateToChangelog"
                >
                  <History class="h-4 w-4" />
                  <span>Historial de Cambios</span>
                </button>

                <button
                  class="w-full flex items-center gap-2 px-2 py-2 rounded text-sm"
                  :class="state.currentView === 'feedback' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'"
                  @click="navigateToFeedback"
                >
                  <MessageCircle class="h-4 w-4" />
                  <span>Enviar Feedback</span>
                </button>

                <Separator class="my-3" />

                <!-- Explorar Guías - Abre Drawer -->
                <button
                  class="w-full flex items-center justify-between px-2 py-2.5 rounded text-sm bg-primary/5 hover:bg-primary/10 border border-primary/20 transition-colors"
                  :class="state.currentModule ? 'ring-2 ring-primary/30' : ''"
                  @click="isModulesDrawerOpen = true"
                >
                  <div class="flex items-center gap-2">
                    <Library class="h-4 w-4 text-primary" />
                    <span class="font-medium">Explorar Guías</span>
                  </div>
                  <ChevronRight class="h-4 w-4 text-primary" />
                </button>

                <!-- Módulo Actual (si hay uno seleccionado) -->
                <template v-if="state.currentModule">
                  <Separator class="my-3" />
                  <p class="text-xs font-medium text-muted-foreground px-2 mb-2">
                    {{ currentModuleTitle.toUpperCase() }}
                  </p>

                  <button
                    v-for="guide in currentModuleGuides"
                    :key="guide.slug"
                    class="w-full flex items-center gap-2 px-2 py-2 rounded hover:bg-muted text-sm text-left"
                    :class="state.currentGuide === guide.slug ? 'bg-primary/10 text-primary' : ''"
                    @click="navigateToGuide(guide.slug)"
                  >
                    <span class="truncate">{{ guide.title }}</span>
                  </button>
                </template>
              </div>
            </ScrollArea>
          </aside>

          <!-- Main Content -->
          <main class="flex-1 min-w-0">
            <ScrollArea class="h-full">
              <div class="max-w-3xl mx-auto p-6">
                <!-- Changelog View -->
                <template v-if="state.currentView === 'changelog'">
                  <ChangelogSection />
                </template>

                <!-- Feedback View -->
                <template v-else-if="state.currentView === 'feedback'">
                  <FeedbackSection />
                </template>

                <!-- News View -->
                <template v-else-if="state.currentView === 'news'">
                  <NewsSection />
                </template>

                <!-- Home View - Mejorado -->
                <template v-else-if="state.currentView === 'home' && !state.currentModule && !state.currentGuide && !state.searchQuery">
                  <!-- Card de Bienvenida -->
                  <Card class="mb-6 overflow-hidden">
                    <div class="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
                      <CardContent class="p-6">
                        <div class="flex items-start gap-4">
                          <div class="h-16 w-16 rounded-2xl overflow-hidden flex-shrink-0 shadow-sm">
                            <!-- <Sparkles class="h-8 w-8 text-primary" /> -->
                            <img src="/img/icons/isotipoblack.svg" alt="Torcaza Projects" class="h-full w-full object-cover" />
                          </div>
                          <div class="flex-1">
                            <h2 class="text-xl font-bold mb-1">¡Gracias por usar Torcaza Projects!</h2>
                            <p class="text-muted-foreground text-sm">
                              Tu plataforma integral para gestión de proyectos, CRM y colaboración.
                              Explora las guías para sacar el máximo provecho de cada funcionalidad.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </div>
                  </Card>

                  <!-- Widgets -->
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <!-- Última versión -->
                    <Card
                      class="cursor-pointer hover:border-primary/50 transition-colors"
                      @click="navigateToChangelog"
                    >
                      <CardContent class="p-4">
                        <div class="flex items-center gap-3 mb-3">
                          <div class="h-9 w-9 rounded-lg bg-green-500/10 flex items-center justify-center">
                            <TrendingUp class="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <p class="text-xs text-muted-foreground">Última actualización</p>
                            <p class="font-semibold">
                              v{{ latestVersion?.version || '...' }}
                            </p>
                          </div>
                        </div>
                        <p v-if="latestVersion" class="text-sm text-muted-foreground line-clamp-2">
                          {{ latestVersion.notes.split('\n')[0] || 'Ver detalles del cambio' }}
                        </p>
                      </CardContent>
                    </Card>

                    <!-- Noticias -->
                    <Card
                      class="cursor-pointer hover:border-primary/50 transition-colors"
                      @click="navigateToNews"
                    >
                      <CardContent class="p-4">
                        <div class="flex items-center gap-3 mb-3">
                          <div class="h-9 w-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                            <Newspaper class="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p class="text-xs text-muted-foreground">Mantente informado</p>
                            <p class="font-semibold">Noticias</p>
                          </div>
                        </div>
                        <p class="text-sm text-muted-foreground">
                          Anuncios, tips y novedades de la plataforma.
                        </p>
                      </CardContent>
                    </Card>

                    <!-- Feedback stats -->
                    <Card
                      class="cursor-pointer hover:border-primary/50 transition-colors"
                      @click="navigateToFeedback"
                    >
                      <CardContent class="p-4">
                        <div class="flex items-center gap-3 mb-3">
                          <div class="h-9 w-9 rounded-lg bg-purple-500/10 flex items-center justify-center">
                            <Heart class="h-4 w-4 text-purple-600" />
                          </div>
                          <div>
                            <p class="text-xs text-muted-foreground">Tu opinión importa</p>
                            <p class="font-semibold">Feedback</p>
                          </div>
                        </div>
                        <!-- Mostrar rating promedio si hay valoraciones -->
                        <div v-if="feedbackStats.ratingCount > 0" class="flex items-center gap-2">
                          <div class="flex">
                            <Star
                              v-for="i in 5"
                              :key="i"
                              class="h-3.5 w-3.5"
                              :class="i <= Math.round(feedbackStats.avgRating)
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-muted-foreground/30'"
                            />
                          </div>
                          <span class="text-sm font-medium">{{ feedbackStats.avgRating.toFixed(1) }}</span>
                        </div>
                        <p v-else class="text-sm text-muted-foreground">
                          {{ feedbackStats.total }} sugerencias
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <!-- Explorar Guías - Card grande -->
                  <Card
                    class="cursor-pointer hover:border-primary/50 transition-colors mb-6"
                    @click="isModulesDrawerOpen = true"
                  >
                    <CardContent class="p-6">
                      <div class="flex items-center justify-between">
                        <div class="flex items-center gap-4">
                          <div class="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Library class="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h3 class="font-semibold text-lg">Explorar Guías</h3>
                            <p class="text-sm text-muted-foreground">
                              {{ availableModules.length }} módulos · {{ availableGuides.length }} guías disponibles
                            </p>
                          </div>
                        </div>
                        <ChevronRight class="h-5 w-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>

                  <!-- Módulos Populares (acceso rápido) -->
                  <h3 class="text-lg font-semibold mb-4">Acceso Rápido</h3>
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <button
                      v-for="mod in availableModules.slice(0, 8)"
                      :key="mod.id"
                      class="flex flex-col items-center gap-2 p-4 border rounded-lg hover:bg-muted hover:border-primary/30 text-center transition-colors"
                      @click="navigateToModule(mod.id)"
                    >
                      <div
                        class="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"
                      >
                        <component :is="getIcon(mod.icon)" class="h-5 w-5 text-primary" />
                      </div>
                      <span class="text-sm font-medium">{{ mod.title }}</span>
                    </button>
                  </div>
                </template>

                <!-- Module View (no guide selected) -->
                <template v-else-if="state.currentView === 'module' || (state.currentModule && !state.currentGuide)">
                  <h2 class="text-2xl font-bold mb-4">{{ currentModuleTitle }}</h2>
                  <div class="space-y-2">
                    <button
                      v-for="guide in currentModuleGuides"
                      :key="guide.slug"
                      class="w-full flex items-start gap-3 p-4 border rounded-lg hover:bg-muted text-left"
                      @click="navigateToGuide(guide.slug)"
                    >
                      <div class="flex-1">
                        <p class="font-medium">{{ guide.title }}</p>
                        <p v-if="guide.description" class="text-sm text-muted-foreground mt-0.5">
                          {{ guide.description }}
                        </p>
                      </div>
                      <ChevronRight class="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                    </button>
                  </div>
                </template>

                <!-- Guide Content -->
                <template v-else-if="currentGuide">
                  <div class="mb-4">
                    <Badge variant="secondary" class="mb-2">{{ currentGuide.category }}</Badge>
                    <div v-if="currentGuide.roles" class="flex gap-1 mt-2 flex-wrap">
                      <Badge v-for="role in currentGuide.roles" :key="role" variant="outline" class="text-xs">
                        {{ roleLabels[role] || role }}
                      </Badge>
                    </div>
                  </div>

                  <div class="prose prose-sm dark:prose-invert max-w-none">
                    <MdPreview :model-value="currentGuide.content" language="en-US" />
                  </div>

                  <!-- Video Embebido -->
                  <div v-if="currentGuide.videoUrl" class="mt-6">
                    <div class="rounded-lg overflow-hidden border bg-muted">
                      <div class="aspect-video">
                        <iframe
                          v-if="isYouTube(currentGuide.videoUrl)"
                          :src="getYouTubeEmbed(currentGuide.videoUrl)"
                          class="w-full h-full"
                          frameborder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowfullscreen
                        />
                        <video
                          v-else
                          :src="currentGuide.videoUrl"
                          controls
                          class="w-full h-full"
                        />
                      </div>
                    </div>
                    <p class="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <Play class="h-3 w-3" />
                      Video demostrativo
                    </p>
                  </div>

                  <!-- GIF Demostrativo -->
                  <div v-if="currentGuide.gifUrl" class="mt-6">
                    <div class="rounded-lg border overflow-hidden bg-muted">
                      <img
                        :src="currentGuide.gifUrl"
                        :alt="currentGuide.title"
                        class="w-full"
                        loading="lazy"
                      />
                    </div>
                    <p class="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <Play class="h-3 w-3" />
                      Animación demostrativa
                    </p>
                  </div>

                  <!-- Action Links (Botones de navegación) -->
                  <div v-if="currentGuide.actionLinks?.length" class="mt-6 pt-4 border-t">
                    <p class="text-sm font-medium mb-3">Acciones rápidas</p>
                    <div class="flex flex-wrap gap-2">
                      <Button
                        v-for="link in currentGuide.actionLinks"
                        :key="link.route"
                        :variant="link.variant || 'outline'"
                        size="sm"
                        @click="handleActionLink(link)"
                      >
                        <component
                          v-if="link.icon"
                          :is="getIcon(link.icon)"
                          class="h-4 w-4 mr-1.5"
                        />
                        {{ link.label }}
                        <ExternalLink class="h-3 w-3 ml-1.5 opacity-50" />
                      </Button>
                    </div>
                  </div>

                  <!-- Related Guides -->
                  <div v-if="relatedGuides.length > 0" class="mt-8 pt-6 border-t">
                    <h4 class="text-sm font-medium mb-3">Guías relacionadas</h4>
                    <div class="flex flex-wrap gap-2">
                      <Button
                        v-for="related in relatedGuides"
                        :key="related.slug"
                        variant="outline"
                        size="sm"
                        @click="navigateToGuide(related.slug)"
                      >
                        {{ related.title }}
                      </Button>
                    </div>
                  </div>

                  <!-- Prev/Next Navigation -->
                  <div class="mt-8 pt-6 border-t flex justify-between">
                    <Button v-if="prevGuide" variant="ghost" @click="navigateToGuide(prevGuide.slug)">
                      <ChevronLeft class="h-4 w-4 mr-1" />
                      {{ prevGuide.title }}
                    </Button>
                    <div v-else />
                    <Button v-if="nextGuide" variant="ghost" @click="navigateToGuide(nextGuide.slug)">
                      {{ nextGuide.title }}
                      <ChevronRight class="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </template>
              </div>
            </ScrollArea>
          </main>
        </div>
      </div>
    </Transition>

    <!-- Modules Drawer -->
    <HelpModulesDrawer
      :is-open="isModulesDrawerOpen && state.isOpen"
      @close="isModulesDrawerOpen = false"
    />
  </Teleport>
</template>

<style>
/* Markdown preview styles */
.md-editor-preview-wrapper {
  padding: 0 !important;
}
.md-editor-preview {
  background: transparent !important;
}
</style>
